import React, { useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import SkipTutorial from "./SkipTutorial";
import {
    finishTutorial,
    getCurrentStepFromLocalStorage,
    moveStepInLocalStorage,
    setStepInLocalStorage,
    StepDirection,
} from "../joyrideStepHelpers";
import { useNavigate, useParams } from "react-router-dom";
import { WarehouseTutorialType } from "../../../../utils/enums";
import { LOCAL_STORAGE_TYPE } from "../constants";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { getHandler } from "../utils/handlerRegistry";

interface JoyrideTooltipContentWrapperProps {
    children: React.ReactNode;
    stepJump?: number;
    route?: string;
    showNext?: boolean;
    nextEnabled?: boolean;
    btnTitle?: string;
    btnNextTutorial?: WarehouseTutorialType.CONSUMABLES_WRITE_OFF | WarehouseTutorialType.PRODUCT_SALES | "Finish";
    hasProducts?: boolean;
    skipCreateProductRout?: string;
    goNext?: () => void;
    goNextId?: string;
}

const JoyrideTooltipContentWrapper: React.FC<JoyrideTooltipContentWrapperProps> = ({
    children,
    stepJump,
    route,
    showNext = false,
    nextEnabled = true,
    btnNextTutorial,
    hasProducts = false,
    skipCreateProductRout,
    goNext,
    goNextId,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);

    const [showSkipTooltip, setShowSkipTooltip] = useState(false);

    const handleNextClick = () => {
        if (stepJump !== undefined) {
            setStepInLocalStorage(stepJump);
            route && navigate(route);
        } else {
            moveStepInLocalStorage(StepDirection.NEXT);
            route && navigate(route);
        }
        const handler = goNextId ? getHandler(goNextId) : null;
        if (typeof handler === "function") {
            try {
                handler();
            } catch (e) {
                console.error("goNext handler error", e);
            }
        }
        goNext && goNext();
        window.dispatchEvent(new Event("warehouseStepChanged"));
    };

    const handleNextOrFinishTutorial = () => {
        switch (btnNextTutorial) {
            case WarehouseTutorialType.CONSUMABLES_WRITE_OFF:
                localStorage.setItem(LOCAL_STORAGE_TYPE, WarehouseTutorialType.CONSUMABLES_WRITE_OFF);
                setStepInLocalStorage(3);
                navigate(`/warehouse/technological-maps/${currentCompanyId}/branch/${branchId}`);
                break;

            case WarehouseTutorialType.PRODUCT_SALES:
                localStorage.setItem(LOCAL_STORAGE_TYPE, WarehouseTutorialType.PRODUCT_SALES);
                setStepInLocalStorage(0);
                navigate(`/warehouse/product/${currentCompanyId}/branch/${branchId}`);
                break;

            case "Finish":
                finishTutorial(branchId, navigate);
                break;

            default:
                console.error("Unknown btnNextTutorial:", btnNextTutorial);
        }
    };

    return (
        <>
            {showSkipTooltip ? (
                <SkipTutorial continueTutorial={() => setShowSkipTooltip(false)} />
            ) : (
                <>
                    <Row>
                        <Col xs={11}>{children}</Col>
                        <Col xs={1} className="p-0 text-center">
                            <Button
                                variant="link"
                                className="p-0"
                                onClick={() => {
                                    const currentStep = getCurrentStepFromLocalStorage();
                                    setShowSkipTooltip(true);
                                    requestAnimationFrame(
                                        () => currentStep === 4 && window.dispatchEvent(new Event("resize"))
                                    );
                                }}
                            >
                                <i className="bi bi-x-lg "></i>
                            </Button>
                        </Col>
                    </Row>
                    {showNext && (
                        <Row className="m-0 mt-3 pt-3 border-top">
                            <Col className="text-end p-0">
                                <Button
                                    variant={nextEnabled ? "primary" : "secondary"}
                                    className="px-4"
                                    onClick={handleNextClick}
                                    disabled={!nextEnabled}
                                >
                                    {t("Next")}
                                </Button>
                            </Col>
                        </Row>
                    )}
                    {btnNextTutorial && (
                        <Row className="m-0 mt-3 pt-3 border-top">
                            <Col className="text-end p-0">
                                <Button variant="primary" className="px-4 w-100" onClick={handleNextOrFinishTutorial}>
                                    {t(`${btnNextTutorial === "Finish" ? "Finish" : `tutorial.${btnNextTutorial}`}`)}
                                </Button>
                            </Col>
                        </Row>
                    )}
                    {hasProducts && (
                        <Row className="m-0 mt-3 pt-3 border-top">
                            <Col className="text-center p-0">
                                <Button
                                    variant="text"
                                    className="p-0 text-grey text-decoration-underline"
                                    onClick={() => {
                                        setStepInLocalStorage(3);
                                        navigate(skipCreateProductRout);
                                    }}
                                >
                                    {t("Skip step")}
                                </Button>
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </>
    );
};

export default JoyrideTooltipContentWrapper;
